import React, { useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiBell, FiGrid, FiLock, FiMenu, FiSettings, FiShield, FiUsers, FiX } from 'react-icons/fi';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthProvider';
import { EMPTY_VALUE, formatEmptyValue } from '../utils/format';

interface LayoutProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', href: '/command-center/mvp-talent', icon: FiGrid },
  { label: 'Talent', href: '/command-center/mvp-talent/talent', icon: FiUsers },
  { label: 'Instances', href: '/command-center/mvp-talent/instances', icon: FiShield },
  { label: 'Work', href: '/command-center/mvp-talent/work', icon: FiAlertCircle },
  { label: 'Notifications', href: '/command-center/mvp-talent/notifications', icon: FiBell },
  { label: 'Security', href: '/command-center/mvp-talent/security', icon: FiLock },
  { label: 'Audit', href: '/command-center/mvp-talent/audit', icon: FiShield },
  { label: 'Settings', href: '/command-center/mvp-talent/settings', icon: FiSettings },
];

export const MvpTalentLayout: React.FC<LayoutProps> = ({ title, subtitle, actions, children }) => {
  const { logout, canAccessCommandCentre, user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.title = `${title} | Core Network`;
  }, [title]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [menuOpen]);

  const availableItems = useMemo(
    () => (canAccessCommandCentre ? NAV_ITEMS : []),
    [canAccessCommandCentre]
  );

  const displayName = user?.first_name || user?.email || 'Core Network user';
  const roleLabel = user?.role || 'user';

  return (
    <div className="mvp-talent-shell">
      {menuOpen ? (
        <button
          type="button"
          className="mvp-talent-sidebar__overlay"
          aria-label="Close Core Network navigation"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}
      <aside className={`mvp-talent-sidebar ${menuOpen ? 'open' : ''}`} aria-label="Core Network sidebar">
        <div className="mvp-talent-sidebar__brand">
          <Link to="/command-center/mvp-talent" className="mvp-talent-sidebar__brand-link" aria-label="Core Network dashboard">
            <div>
              <h1>Core Network</h1>
            </div>
          </Link>
        </div>

        <nav id="core-network-sidebar-navigation" className="mvp-talent-sidebar__nav" aria-label="Core Network navigation">
          {availableItems.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === '/command-center/mvp-talent'}
              className={({ isActive }) => `mvp-talent-sidebar__link ${isActive ? 'active' : ''}`}
            >
              <item.icon className="mvp-talent-sidebar__icon" aria-hidden />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mvp-talent-sidebar__footer">
          <div className="mvp-talent-sidebar__user-card">
            <div className="mvp-talent-sidebar__user-avatar" aria-hidden="true">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="mvp-talent-user-name">{displayName}</div>
              <div className="mvp-talent-user-role">{roleLabel}</div>
            </div>
          </div>
          <button type="button" className="mvp-button mvp-button--ghost mvp-talent-sidebar__signout" onClick={logout}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="mvp-talent-main">
        <header className="mvp-talent-topbar">
          <button
            type="button"
            className="mvp-talent-menu-button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Close Core Network navigation' : 'Open Core Network navigation'}
            aria-expanded={menuOpen}
            aria-controls="core-network-sidebar-navigation"
          >
            {menuOpen ? <FiX aria-hidden /> : <FiMenu aria-hidden />}
            <span>{menuOpen ? 'Close' : 'Menu'}</span>
          </button>
          <div className="mvp-talent-topbar__intro">
            <p className="mvp-talent-topbar__eyebrow">Platform Control Plane</p>
            <h2>{title}</h2>
            {subtitle ? <p className="mvp-talent-topbar__subtitle">{subtitle}</p> : null}
          </div>
          <div className="mvp-talent-topbar__actions">{actions}</div>
        </header>

        <main className="mvp-talent-content">{children}</main>
      </div>
    </div>
  );
};

export const MvpCard: React.FC<{ title?: string; children: React.ReactNode; className?: string }> = ({ title, children, className = '' }) => (
  <section className={`mvp-card ${className}`.trim()}>
    {title ? <h3 className="mvp-card__title">{title}</h3> : null}
    {children}
  </section>
);

export const MvpMetricCard: React.FC<{ label: string; value: React.ReactNode; tone?: 'default' | 'warning' | 'danger' }> = ({ label, value, tone = 'default' }) => (
  <div className={`mvp-metric mvp-metric--${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

export const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  const normalized = String(status || 'unknown').toLowerCase();
  return <span className={`mvp-badge mvp-badge--${normalized.replace(/[^a-z0-9]+/g, '-')}`}>{status || 'Unknown'}</span>;
};

export const LoadingState: React.FC<{ label?: string }> = ({ label = 'Loading...' }) => <div className="mvp-state">{label}</div>;

export const EmptyState: React.FC<{ title: string; message: string; action?: React.ReactNode }> = ({ title, message, action }) => (
  <div className="mvp-state">
    <h3>{title}</h3>
    <p>{message}</p>
    {action}
  </div>
);

export const ErrorState: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div className="mvp-state mvp-state--error">
    <h3>Unable to load data</h3>
    <p>{message}</p>
    {onRetry ? (
      <button type="button" className="mvp-button" onClick={onRetry}>
        Try again
      </button>
    ) : null}
  </div>
);

export const FeedbackBanner: React.FC<{ tone: 'success' | 'error'; message: string; onDismiss?: () => void }> = ({ tone, message, onDismiss }) => (
  <div className={`mvp-feedback mvp-feedback--${tone}`} role="status">
    <span>{message}</span>
    {onDismiss ? (
      <button type="button" className="mvp-button mvp-button--inline" onClick={onDismiss}>
        Dismiss
      </button>
    ) : null}
  </div>
);

export const DataTable: React.FC<{ columns: string[]; children: React.ReactNode }> = ({ columns, children }) => (
  <div className="mvp-table-wrap">
    <table className="mvp-table">
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export const PaginationControls: React.FC<{ page: number; totalPages: number; onPageChange: (nextPage: number) => void }> = ({ page, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="mvp-pagination">
      <button type="button" className="mvp-button mvp-button--ghost" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
        Previous
      </button>
      <span>
        Page {page} of {totalPages}
      </span>
      <button type="button" className="mvp-button mvp-button--ghost" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
        Next
      </button>
    </div>
  );
};

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  requireReason?: boolean;
  confirmTone?: 'default' | 'danger';
  onClose: () => void;
  onConfirm: (payload: { reason: string; confirmationText: string }) => Promise<void> | void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  requireReason = true,
  confirmTone = 'default',
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason('');
      setConfirmationText('');
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="mvp-dialog-backdrop" role="presentation" onClick={onClose}>
      <div className="mvp-dialog" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <h3>{title}</h3>
        <p>{description}</p>
        {requireReason ? (
          <label className="mvp-field">
            <span>Reason</span>
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={4} />
          </label>
        ) : null}
        <label className="mvp-field">
          <span>Type CONFIRM to continue</span>
          <input value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} />
        </label>
        <div className="mvp-dialog__actions">
          <button type="button" className="mvp-button mvp-button--ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className={`mvp-button ${confirmTone === 'danger' ? 'mvp-button--danger' : ''}`.trim()}
            disabled={submitting || confirmationText !== 'CONFIRM' || (requireReason && !reason.trim())}
            onClick={async () => {
              setSubmitting(true);
              try {
                await onConfirm({ reason: reason.trim(), confirmationText: confirmationText.trim() });
                onClose();
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitting ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const FilterGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mvp-filter-grid">{children}</div>
);

export const FieldGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="mvp-field">
    <span>{label}</span>
    {children}
  </label>
);

export const KeyValueGrid: React.FC<{ items: Array<{ label: string; value: React.ReactNode }> }> = ({ items }) => (
  <dl className="mvp-key-value-grid">
    {items.map((item) => (
      <React.Fragment key={item.label}>
        <dt>{item.label}</dt>
        <dd>{item.value === undefined || item.value === null || item.value === '' ? EMPTY_VALUE : item.value}</dd>
      </React.Fragment>
    ))}
  </dl>
);

export const InlineActions: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className="mvp-inline-actions">{children}</div>;

export const RouteLinkCard: React.FC<{ to: string; title: string; description: string }> = ({ to, title, description }) => (
  <Link to={to} className="mvp-route-card">
    <strong>{title}</strong>
    <span>{formatEmptyValue(description)}</span>
  </Link>
);
